import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getBlogPost } from "@/lib/blog/blog.functions";
import { Logo } from "@/components/intel/Logo";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const post = await getBlogPost({ data: { slug: params.slug } });
    if (!post) throw notFound();
    return post;
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Not found" }] };
    const url = `https://carionexus.com/blog/${params.slug}`;
    const scripts: Array<{ type: string; children: string }> = [];
    // Article JSON-LD (built from row data)
    scripts.push({
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: loaderData.title,
        description: loaderData.description,
        datePublished: loaderData.published_at,
        dateModified: loaderData.updated_at,
        author: { "@type": "Organization", name: loaderData.author },
        publisher: { "@type": "Organization", name: "CARIO INTEL", url: "https://carionexus.com" },
        keywords: (loaderData.keywords ?? []).join(", "),
        mainEntityOfPage: url,
      }),
    });
    if (loaderData.jsonld_str) {
      scripts.push({ type: "application/ld+json", children: loaderData.jsonld_str });
    }
    return {
      meta: [
        { title: `${loaderData.title} — CARIO INTEL` },
        { name: "description", content: loaderData.description },
        { name: "keywords", content: (loaderData.keywords ?? []).join(", ") },
        { property: "og:title", content: loaderData.title },
        { property: "og:description", content: loaderData.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "article:published_time", content: loaderData.published_at },
        { property: "article:modified_time", content: loaderData.updated_at },
        { property: "article:section", content: loaderData.category },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: loaderData.title },
        { name: "twitter:description", content: loaderData.description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts,
    };
  },
  errorComponent: ({ error }) => (
    <div className="p-8 text-sm text-muted-foreground">Failed to load: {error.message}</div>
  ),
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="glass rounded-2xl p-10 text-center">
        <p className="font-mono text-xs text-cyan tracking-[0.3em]">404 · BRIEFING_MISSING</p>
        <h1 className="mt-3 font-display text-3xl">Not found</h1>
        <Link to="/blog" className="mt-6 inline-block text-sm text-cyan underline">Back to briefings</Link>
      </div>
    </div>
  ),
  component: BlogPostPage,
});

function BlogPostPage() {
  const post = Route.useLoaderData();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-panel-border bg-background/70 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-4xl items-center px-4 sm:px-6">
          <Logo />
          <nav className="ml-auto flex items-center gap-4 text-sm">
            <Link to="/blog" className="text-muted-foreground hover:text-foreground">Briefings</Link>
            <Link to="/pricing" className="text-muted-foreground hover:text-foreground">Pricing</Link>
          </nav>
        </div>
      </header>

      <article className="mx-auto max-w-3xl px-4 py-12 sm:py-16">
        <div className="flex items-center gap-3 text-xs font-mono uppercase tracking-widest text-muted-foreground">
          <span className="text-cyan">{post.category}</span>
          <span>·</span>
          <time dateTime={post.published_at}>
            {new Date(post.published_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
          </time>
          <span>·</span>
          <span>{post.reading_minutes} min read</span>
          <span>·</span>
          <span>{post.author}</span>
        </div>
        <h1 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">{post.title}</h1>
        <p className="mt-3 text-lg text-muted-foreground">{post.description}</p>

        <div className="prose prose-invert prose-cyan mt-10 max-w-none prose-headings:font-display prose-a:text-cyan prose-strong:text-foreground prose-code:text-cyan">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content_md}</ReactMarkdown>
        </div>

        <div className="mt-14 border-t border-panel-border pt-6 text-sm text-muted-foreground">
          Published by the CARIO Intel Desk ·{" "}
          <Link to="/blog" className="text-cyan underline">More briefings</Link>
        </div>
      </article>
    </div>
  );
}